/**
 * SOAEmailTab — Phase J
 * Inside the SOA Generation panel: a "Email to Investors" view that lists
 * accounts with a generated SOA on the selected fund and bulk-sends the
 * PDFs by email using the same composer UX as Drawdown Emails / Reminders.
 *
 * Filter tabs: All / Email Sent / Never Sent (based on each row's most-
 * recent SOA email status).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Mail, Send, FileText, AlertCircle, CheckCircle, Loader2, X, StopCircle,
  Paperclip, RefreshCw, Eye, EyeOff, Trash2, RotateCcw
} from 'lucide-react';
import EmailPreviewModal from '../email/EmailPreviewModal';
import TeamMemberPicker from '../email/TeamMemberPicker';
import RecipientSearchBar, { filterByQuery } from '../email/RecipientSearchBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:    { cls: 'bg-gray-100 text-gray-700',   label: 'Pending' },
  running:    { cls: 'bg-blue-100 text-blue-700',   label: 'Sending...' },
  completed:  { cls: 'bg-green-100 text-green-800', label: 'Completed' },
  aborted:    { cls: 'bg-yellow-100 text-yellow-800', label: 'Aborted' },
  failed:     { cls: 'bg-red-100 text-red-800',     label: 'Failed' },
};
const REC_STATUS_META = {
  pending: { cls: 'bg-gray-100 text-gray-600',   label: 'Pending' },
  sent:    { cls: 'bg-green-100 text-green-700', label: 'Sent' },
  failed:  { cls: 'bg-red-100 text-red-700',     label: 'Failed' },
  skipped: { cls: 'bg-yellow-100 text-yellow-700', label: 'Skipped' },
  aborted: { cls: 'bg-yellow-100 text-yellow-700', label: 'Aborted' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
};

const SOAEmailTab = ({ fundId, currentUser }) => {
  const [filter, setFilter] = useState('all');
  const [counts, setCounts] = useState({ all: 0, sent: 0, never: 0, hidden: 0 });
  const [showHidden, setShowHidden] = useState(false);
  const [togglingRowId, setTogglingRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [bccSelf, setBccSelf] = useState(false);
  const [dateFormat, setDateFormat] = useState('dd mmmm yyyy');
  const [numberFormat, setNumberFormat] = useState('western');
  // "as on" date the SOAs represent — flows into the {{soa_as_of_*}} placeholders.
  const [asOfDate, setAsOfDate] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [bccUserIds, setBccUserIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!fundId) return;
    setSelectedIds([]);
    loadRows();
    loadTemplates();
    loadHistory();
    // eslint-disable-next-line
  }, [fundId, filter]);

  // poll active job
  useEffect(() => {
    if (!activeJob) return;
    if (['completed', 'aborted', 'failed'].includes(activeJob.status)) return;
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/funds/${fundId}/soa-emails/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (['completed', 'aborted', 'failed'].includes(data.status)) {
          loadHistory();
          loadRows();
        }
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status, fundId]);

  const loadRows = async () => {
    if (!fundId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/funds/${fundId}/soa-emails/recipients?filter=${filter}&include_hidden=true`);
      setRows(data.rows || []);
      setCounts(data.counts || { all: 0, sent: 0, never: 0, hidden: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/email-templates?scope=approved&section=soa_delivery`);
      // SOA emails: only general + investor_account context allowed (per backend guard).
      setTemplates((data || []).filter(t => ['general', 'investor_account'].includes(t.context_type)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load approved templates');
    }
  };

  const loadHistory = async () => {
    if (!fundId) return;
    try {
      const { data } = await axios.get(`${API}/funds/${fundId}/soa-emails/history`);
      setHistory(data || []);
    } catch { /* ignore */ }
  };

  const toggleRow = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  // Search-aware select-all (matches the dd-emails / investor-comm UX).
  const [recipientSearch, setRecipientSearch] = useState('');
  const visibleRows = useMemo(() => {
    const base = showHidden ? rows : rows.filter(r => !r.hidden_from_email);
    return filterByQuery(base, recipientSearch, ['account_name', 'pan']);
  }, [rows, recipientSearch, showHidden]);

  const handleHideToggle = async (row) => {
    const isHidden = !!row.hidden_from_email;
    if (!isHidden) {
      const ok = window.confirm(
        `Remove "${row.account_name}" from the SOA email send list?\n\n` +
        `The investor's generated SOA documents stay intact — only the email send list is affected. ` +
        `Toggle "Show hidden" to restore later.`
      );
      if (!ok) return;
    }
    setTogglingRowId(row.account_id);
    setError(''); setSuccess('');
    try {
      const path = isHidden ? 'unhide' : 'hide';
      await axios.post(`${API}/funds/${fundId}/soa-emails/recipients/${row.account_id}/${path}`);
      setSelectedIds(prev => prev.filter(id => id !== row.account_id));
      setSuccess(isHidden ? `Restored "${row.account_name}".` : `Removed "${row.account_name}" from the list.`);
      await loadRows();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update row');
    } finally {
      setTogglingRowId(null);
    }
  };

  const toggleAll = () => {
    const ids = visibleRows.filter(r => !r.hidden_from_email).map(r => r.account_id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id));
    if (allSelected) setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    else setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const handleSend = async () => {
    setError(''); setSuccess('');
    if (!selectedTemplate) { setError('Pick an approved template first.'); return; }
    if (selectedIds.length === 0) { setError('Select at least one account.'); return; }
    try {
      const { data } = await axios.post(`${API}/funds/${fundId}/soa-emails/send`, {
        template_id: selectedTemplate,
        account_ids: selectedIds,
        bcc_self: bccSelf,
        date_format: dateFormat,
        number_format: numberFormat,
        cc_user_ids: ccUserIds,
        bcc_user_ids: bccUserIds,
        as_of_date: asOfDate || null,
      });
      const { data: job } = await axios.get(`${API}/funds/${fundId}/soa-emails/jobs/${data.job_id}`);
      setActiveJob(job);
      setSelectedIds([]);
      const skipped = data.skipped_no_soa || [];
      setSuccess(
        `Queued ${data.total} SOA email(s).` +
        (skipped.length ? ` Skipped ${skipped.length} account(s) without a generated SOA.` : '')
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start send');
    }
  };

  const handleAbort = async () => {
    if (!activeJob) return;
    if (!window.confirm('Abort sending? Already-sent emails cannot be unsent.')) return;
    try {
      await axios.post(`${API}/funds/${fundId}/soa-emails/jobs/${activeJob.id}/abort`);
      setSuccess('Abort requested. Stopping after the current recipient...');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to abort'); }
  };

  const senderEnabled = !!currentUser?.email_sending_enabled;
  const sendingActive = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const progressPct = activeJob && activeJob.total > 0
    ? Math.round(((activeJob.sent || 0) + (activeJob.failed || 0) + (activeJob.skipped || 0)) / activeJob.total * 100)
    : 0;
  const allSelectedOnPage = rows.length > 0 && rows.every(r => selectedIds.includes(r.account_id));

  const previewRecipients = useMemo(() => rows
    .filter(r => selectedIds.includes(r.account_id))
    .map(r => ({ id: r.account_id, account_name: r.account_name, folio_no: r.folio_no })),
    [rows, selectedIds]);

  if (!fundId) {
    return (
      <div className="p-8 text-center text-sm text-gray-400" data-testid="soa-email-no-fund">
        <Mail className="mx-auto text-gray-300 mb-2" size={32} />
        Select a fund first to email Statements of Account.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="soa-email-tab">
      {!senderEnabled && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-testid="soa-email-disabled-notice">
          <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">You can't send emails from your account yet.</p>
            <p className="mt-1">Ask an Admin to enable "Allow this user to send emails" for you in <strong>User Management</strong>, then save your SMTP credentials in <Link className="underline text-blue-700" to="/email-config">Email Configuration</Link>.</p>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 flex items-center mb-4">
          <Mail size={16} className="mr-2 text-blue-600" /> Send Statement of Account by Email
        </h3>

        {error && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800 flex items-start" data-testid="soa-email-error">
            <AlertCircle size={14} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center" data-testid="soa-email-success">
            <CheckCircle size={14} className="text-green-600 mr-2" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approved Template *
              <span className="text-gray-400 ml-1">(General or Investor-Account context only)</span>
            </label>
            <select
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="soa-email-template-select"
            >
              <option value="">— Pick a template —</option>
              {templates.length === 0 && <option disabled>(no approved General/Investor-Account templates yet)</option>}
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name} · {t.context_type === 'general' ? 'General' : 'Investor Account'}</option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Need to <Link className="text-blue-600 underline" to="/email-templates/new">create one</Link> first and have an Admin approve it. Make sure it has the <strong>SOA</strong> dynamic attachment enabled.
              </p>
            )}
          </div>
          <div className="space-y-2 pt-1">
            <p className="text-xs text-gray-700 flex items-start">
              <Paperclip size={12} className="inline mr-1 mt-0.5 text-gray-400 flex-shrink-0" />
              <span>The most-recent generated SOA PDF is auto-attached for each selected account. Templates without the <strong>SOA</strong> dynamic attachment will send without the PDF.</span>
            </p>
            <label className="flex items-center cursor-pointer">
              <input type="checkbox" checked={bccSelf} onChange={e => setBccSelf(e.target.checked)} className="h-4 w-4 text-blue-600 rounded" data-testid="soa-email-bcc-self" />
              <span className="ml-2 text-sm text-gray-700">BCC me on every email</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              SOA &quot;As On&quot; Date
              <span className="text-gray-400 ml-1">(populates {`{{soa_as_of_date}}`} / {`{{soa_as_of_month_year}}`} placeholders)</span>
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={e => setAsOfDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="soa-email-as-of-date"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date Format <span className="text-gray-400 ml-1">(date merge fields)</span></label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="soa-email-date-format-select">
              <option value="dd mmmm yyyy">25 January 2026</option>
              <option value="dd-mm-yyyy">25-01-2026</option>
              <option value="mm-dd-yyyy">01-25-2026</option>
              <option value="dd/mm/yyyy">25/01/2026</option>
              <option value="mm/dd/yyyy">01/25/2026</option>
              <option value="yyyy-mm-dd">2026-01-25</option>
              <option value="mmmm dd, yyyy">January 25, 2026</option>
              <option value="dd-mmmm-yyyy">25-January-2026</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Number Format <span className="text-gray-400 ml-1">(numeric merge fields)</span></label>
            <select value={numberFormat} onChange={e => setNumberFormat(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              data-testid="soa-email-number-format-select">
              <option value="western">Western (1,000,000)</option>
              <option value="indian">Indian (10,00,000)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <TeamMemberPicker
            label="CC team members"
            value={ccUserIds}
            onChange={setCcUserIds}
            placeholder="Add teammates to Cc on every email…"
            testid="soa-email-cc-team"
          />
          <TeamMemberPicker
            label="BCC team members"
            value={bccUserIds}
            onChange={setBccUserIds}
            placeholder="Add teammates to Bcc on every email…"
            testid="soa-email-bcc-team"
          />
        </div>

        {/* Filter tabs + recipient table */}
        <div className="flex flex-wrap items-center justify-between mb-2">
          <div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
            {[
              { id: 'all',   label: 'All',           count: counts.all },
              { id: 'sent',  label: 'Email Sent',    count: counts.sent },
              { id: 'never', label: 'Never Sent',    count: counts.never },
            ].map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => { setFilter(t.id); setSelectedIds([]); }}
                role="tab"
                aria-selected={filter === t.id}
                className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors ${
                  filter === t.id ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`soa-email-filter-${t.id}`}
              >
                {t.label} <span className="ml-1 text-xs text-gray-400">({t.count})</span>
              </button>
            ))}
          </div>
          <button onClick={loadRows} className="text-xs text-gray-500 hover:text-gray-800 flex items-center" title="Refresh" data-testid="soa-email-refresh">
            <RefreshCw size={12} className="mr-1" /> Refresh
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-semibold text-gray-700">Recipients ({selectedIds.length} of {rows.length} on this page)</span>
            <div className="flex items-center gap-3 flex-1 min-w-[200px] justify-end flex-wrap">
              <label className="flex items-center cursor-pointer text-xs text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={e => setShowHidden(e.target.checked)}
                  className="h-3.5 w-3.5 text-blue-600 rounded mr-1.5"
                  data-testid="soa-email-show-hidden-toggle"
                />
                <span className="flex items-center"><EyeOff size={12} className="mr-1 text-gray-500" />Show hidden ({counts.hidden || 0})</span>
              </label>
              <div className="flex items-center gap-2 min-w-[200px] max-w-[400px]">
                <RecipientSearchBar value={recipientSearch} onChange={setRecipientSearch} placeholder="Search account name, PAN…" testid="soa-email-recipient-search" className="flex-1" />
                <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid="soa-email-select-all" disabled={visibleRows.length === 0}>
                  {visibleRows.length > 0 && visibleRows.filter(r => !r.hidden_from_email).every(r => selectedIds.includes(r.account_id)) ? 'Clear visible' : 'Select all visible'}
                </button>
              </div>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <p className="p-4 text-sm text-gray-500 italic text-center"><Loader2 size={14} className="inline animate-spin mr-1" />Loading...</p>
            ) : rows.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 italic text-center">
                {filter === 'all'
                  ? 'No accounts have a generated SOA on this fund yet. Generate them on the "Generate SOA" tab first.'
                  : filter === 'sent'
                  ? 'No SOA emails have been successfully sent yet on this fund.'
                  : 'Every account with a generated SOA has already been emailed.'}
              </p>
            ) : visibleRows.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 italic text-center" data-testid="soa-email-recipients-empty">
                No accounts match "{recipientSearch}".
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {visibleRows.map(r => {
                    const stMeta = r.last_email_status ? (REC_STATUS_META[r.last_email_status] || REC_STATUS_META.pending) : null;
                    const isHidden = !!r.hidden_from_email;
                    const isToggling = togglingRowId === r.account_id;
                    const isAdmin = currentUser?.role === 'admin';
                    return (
                    <tr key={r.account_id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${isHidden ? 'bg-gray-50 opacity-70' : ''}`} data-testid={`soa-email-row-${r.account_id}`}>
                      <td className="px-3 py-2 w-8">
                        <input type="checkbox"
                          checked={selectedIds.includes(r.account_id)}
                          onChange={() => toggleRow(r.account_id)}
                          disabled={isHidden}
                          title={isHidden ? 'Restore this row before selecting for send' : ''}
                          data-testid={`soa-email-pick-${r.account_id}`}
                          className="h-4 w-4 text-blue-600 rounded disabled:opacity-40 disabled:cursor-not-allowed" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{r.account_name}</span>
                          {isHidden && (
                            <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold bg-gray-200 text-gray-700 rounded" data-testid={`soa-email-hidden-badge-${r.account_id}`}>
                              Hidden
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {(r.class_of_units || []).map(c => <span key={c} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded mr-1">{c}</span>)}
                          {r.folio_no && <span className="mr-1">Folio: {r.folio_no}</span>}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-md mt-0.5">
                          {(r.to_emails || []).length > 0 ? (
                            <div><span className="font-semibold">To:</span> {(r.to_emails || []).join(', ')}</div>
                          ) : <span className="text-red-600 italic">no primary email</span>}
                          {(r.cc_emails || []).length > 0 && (
                            <div><span className="font-semibold">Cc:</span> {(r.cc_emails || []).join(', ')}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        <div className="flex items-center text-gray-700"><FileText size={11} className="mr-1 text-gray-400" /> SOA: {fmtDate(r.soa_generated_at)}</div>
                        {stMeta ? (
                          <div className="mt-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${stMeta.cls}`}>{stMeta.label}</span>
                            {r.last_email_sent_at && <span className="ml-1 text-gray-400">{fmtDate(r.last_email_sent_at)}</span>}
                          </div>
                        ) : <div className="text-gray-400 italic mt-1">never sent</div>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap w-24">
                        {isAdmin ? (
                          isHidden ? (
                            <button
                              onClick={() => handleHideToggle(r)}
                              disabled={isToggling}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 disabled:opacity-50"
                              data-testid={`soa-email-restore-${r.account_id}`}
                              title="Restore this investor to the SOA email send list"
                            >
                              {isToggling ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                              Restore
                            </button>
                          ) : (
                            <button
                              onClick={() => handleHideToggle(r)}
                              disabled={isToggling}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200 disabled:opacity-50"
                              data-testid={`soa-email-hide-${r.account_id}`}
                              title="Remove this investor from the SOA email send list (SOA documents unaffected)"
                            >
                              {isToggling ? <Loader2 size={12} className="animate-spin mr-1" /> : <Trash2 size={12} className="mr-1" />}
                              Delete
                            </button>
                          )
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={!selectedTemplate || selectedIds.length === 0}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="soa-email-preview-btn"
            title="Preview the SOA email for one selected account"
          >
            <Eye size={14} className="mr-2" /> Preview
          </button>
          <button
            onClick={handleSend}
            disabled={!senderEnabled || sendingActive || templates.length === 0 || selectedIds.length === 0 || !selectedTemplate}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="soa-email-send-btn"
          >
            <Send size={14} className="mr-2" /> Send SOA to {selectedIds.length || '...'} Account{selectedIds.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      <EmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        drawdownId={fundId}
        previewPath={`funds/${fundId}/soa-emails/preview`}
        testSendPath={`funds/${fundId}/soa-emails/test-send`}
        templateId={selectedTemplate}
        kind="soa_email"
        recipientFieldName="account_id"
        recipients={previewRecipients}
        bccSelf={bccSelf}
        dateFormat={dateFormat}
        numberFormat={numberFormat}
        ccUserIds={ccUserIds}
        bccUserIds={bccUserIds}
        asOfDate={asOfDate}
      />

      {/* Active job */}
      {activeJob && (
        <div className="bg-white border border-blue-300 rounded-lg p-5" data-testid="soa-email-active-job">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Loader2 size={14} className={`mr-2 ${sendingActive ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
              {sendingActive ? 'Sending SOA emails...' : 'Last send result'}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-full font-medium ${(STATUS_META[activeJob.status] || STATUS_META.pending).cls}`} data-testid="soa-email-job-status">
                {(STATUS_META[activeJob.status] || STATUS_META.pending).label}
              </span>
              {sendingActive && (
                <button onClick={handleAbort} className="flex items-center bg-red-50 text-red-700 px-2 py-1 rounded hover:bg-red-100" data-testid="soa-email-abort-btn">
                  <StopCircle size={12} className="mr-1" /> Abort
                </button>
              )}
              <button onClick={() => setActiveJob(null)} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2"><div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} /></div>
          <p className="text-xs text-gray-600 text-center">{activeJob.sent || 0} sent · {activeJob.failed || 0} failed · {activeJob.skipped || 0} skipped · of {activeJob.total} total</p>
          {!sendingActive && (activeJob.recipients || []).length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto border border-gray-100 rounded">
              <table className="w-full text-xs">
                <tbody>
                  {(activeJob.recipients || []).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-2 py-1 text-gray-700">{r.account_name}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded ${(REC_STATUS_META[r.status] || REC_STATUS_META.pending).cls}`}>
                          {(REC_STATUS_META[r.status] || REC_STATUS_META.pending).label}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-gray-500">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="soa-email-history">
          <h3 className="text-sm font-bold text-gray-900 flex items-center mb-3">
            <FileText size={14} className="mr-2 text-gray-500" /> SOA Email Send History
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Failed</th>
                  <th className="px-3 py-2 text-right">Skipped</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(j => (
                  <tr key={j.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-700">{fmtDate(j.created_at)}</td>
                    <td className="px-3 py-2 text-gray-700">{j.template_name || '—'}</td>
                    <td className="px-3 py-2 text-right">{j.total}</td>
                    <td className="px-3 py-2 text-right text-green-700 font-medium">{j.sent}</td>
                    <td className="px-3 py-2 text-right text-red-700 font-medium">{j.failed}</td>
                    <td className="px-3 py-2 text-right text-yellow-700 font-medium">{j.skipped}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${(STATUS_META[j.status] || STATUS_META.pending).cls}`}>{(STATUS_META[j.status] || STATUS_META.pending).label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOAEmailTab;
